import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logSecurityEvent } from '../services/rbacService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Session timeouts (in ms)
const LOCK_TIMEOUT = 15 * 60 * 1000;   // 15 minutes → lock session
const LOGOUT_TIMEOUT = 30 * 60 * 1000; // 30 minutes → auto logout

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [sessionLocked, setSessionLocked] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)

  const lockTimerRef = useRef(null)
  const logoutTimerRef = useRef(null)
  const lastActivityRef = useRef(Date.now())

  // ─── Profile operations ───────────────────────────────────
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error && data) {
          setUserProfile(data)
        } else {
          // Fallback: check admin_users table
          const { data: adminData } = await supabase?.from('admin_users')?.select('*')?.eq('id', userId)?.single()
          if (adminData) setUserProfile({ ...adminData, role: adminData?.role || 'admin' })
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // ─── Session timers ───────────────────────────────────────
  const clearTimers = useCallback(() => {
    if (lockTimerRef?.current) clearTimeout(lockTimerRef?.current)
    if (logoutTimerRef?.current) clearTimeout(logoutTimerRef?.current)
  }, [])

  const resetTimers = useCallback(() => {
    clearTimers()
    lastActivityRef.current = Date.now()

    lockTimerRef.current = setTimeout(() => {
      setSessionLocked(true)
    }, LOCK_TIMEOUT)

    logoutTimerRef.current = setTimeout(async () => {
      setSessionExpired(true)
      setSessionLocked(false)
      await supabase?.auth?.signOut()
      setUser(null)
      profileOperations?.clear()
    }, LOGOUT_TIMEOUT)
  }, [clearTimers])

  // ─── Activity listeners ───────────────────────────────────
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    const handleActivity = () => {
      if (user && !sessionExpired) {
        if (sessionLocked) return // Don't reset timers when locked
        lastActivityRef.current = Date.now()
        resetTimers()
      }
    }
    events?.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }))
    return () => events?.forEach((e) => window.removeEventListener(e, handleActivity));
  }, [user, sessionLocked, sessionExpired, resetTimers])

  // ─── Auth state handlers ──────────────────────────────────
  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)

      if (session?.user) {
        profileOperations?.load(session?.user?.id)
        setSessionLocked(false)
        setSessionExpired(false)
        resetTimers()
      } else {
        profileOperations?.clear()
        clearTimers()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => {
      subscription?.unsubscribe()
      clearTimers()
    }
  }, [])

  // ─── Auth methods ─────────────────────────────────────────
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })

      if (!error && data?.user) {
        // Check if user is active
        const { data: profile } = await supabase?.from('user_profiles')?.select('is_active, role')?.eq('id', data?.user?.id)?.single()
        if (profile && profile?.is_active === false) {
          await supabase?.auth?.signOut()
          return { data: null, error: { message: 'Votre compte a été désactivé. Contactez l\'administrateur.' } }
        }

        // Log successful login
        await logSecurityEvent({
          userId: data?.user?.id,
          userEmail: email,
          eventType: 'login_success',
          details: { role: profile?.role },
        })

        // Update last_login_at
        await supabase?.from('user_profiles')?.update({ last_login_at: new Date()?.toISOString() })?.eq('id', data?.user?.id)
      } else if (error) {
        // Log failed login
        await logSecurityEvent({
          userEmail: email,
          eventType: 'login_failed',
          details: { reason: error?.message },
        })
      }

      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      if (user) {
        await logSecurityEvent({
          userId: user?.id,
          userEmail: user?.email,
          eventType: 'logout',
        })
      }
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
        clearTimers()
        setSessionLocked(false)
        setSessionExpired(false)
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const unlockSession = async (password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: user?.email,
        password,
      })
      if (!error) {
        setSessionLocked(false)
        resetTimers()
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Mot de passe incorrect.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // ─── Role helpers ─────────────────────────────────────────
  const userRole = userProfile?.role || null
  const isSuperAdmin = userRole === 'super_admin'
  const isAdminOrSuper = ['super_admin', 'admin']?.includes(userRole)

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    unlockSession,
    isAuthenticated: !!user,
    // Role info
    userRole,
    isSuperAdmin,
    isAdminOrSuper,
    // Session state
    sessionLocked,
    sessionExpired,
    setSessionLocked,
    setSessionExpired,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
